import {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from 'react';

import { Textarea } from '@/components/ui/textarea';
import { useAssignablePeople } from '@/features/identity/hooks/useAssignablePeople';
import { displayNameOf } from '@/shared/components/accountIdentity';
import type { UserSummary } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';

import { activeQuery, insertMention, type MentionedPerson } from '../mentions';

function nameOf(person: UserSummary): string {
  return displayNameOf(person.display_name, person.email);
}

/** The most people the picker offers at once. */
const MAX_OFFERED = 8;

/**
 * A comment box that knows about @mentions (spec 137 US1). Typing `@` offers
 * the people who can be mentioned — the gate-assignee directory, since every
 * active person can reach every workspace — and choosing one puts `@Name`
 * into the text and reports the person to the owner, who turns the names
 * still present into mentions when the comment is sent.
 *
 * The list is a listbox tied to the textarea by `aria-activedescendant`, so the
 * caret never leaves the text: arrows move through it, Enter or Tab chooses,
 * Escape closes it until the next `@`.
 */
export function MentionTextarea({
  value,
  onValueChange,
  onMention,
  onKeyDown,
  workspaceId,
  ...props
}: Omit<ComponentProps<'textarea'>, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (value: string) => void;
  onMention: (person: MentionedPerson) => void;
  /** The workspace whose people may be mentioned (spec 186). */
  workspaceId?: string;
}) {
  const listId = useId();
  const textarea = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  // The `@` the reader closed the list on, or chose someone from, and what
  // followed it then. The list stays closed while the text after that `@` still
  // begins the same way — typing on after a chosen name — and opens again once
  // it does not: the name deleted into, or deleted and an `@` typed afresh in
  // the same place. A position alone kept a re-typed `@` closed for good.
  const [dismissed, setDismissed] = useState<{
    start: number;
    query: string;
  } | null>(null);
  const pendingCaret = useRef<number | null>(null);
  const directory = useAssignablePeople(workspaceId);

  const mention = activeQuery(value, caret);
  const offered = useMemo(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return directory.people
      .filter(
        (person) =>
          nameOf(person).toLowerCase().includes(q) ||
          person.email.toLowerCase().includes(q),
      )
      .slice(0, MAX_OFFERED);
  }, [mention, directory.people]);
  const open =
    mention !== null &&
    !(
      dismissed !== null &&
      mention.start === dismissed.start &&
      mention.query.startsWith(dismissed.query)
    );

  // Put the caret after an inserted name once React has written the text.
  useLayoutEffect(() => {
    if (pendingCaret.current === null || !textarea.current) return;
    textarea.current.setSelectionRange(
      pendingCaret.current,
      pendingCaret.current,
    );
    setCaret(pendingCaret.current);
    pendingCaret.current = null;
  }, [value]);

  function choose(index: number) {
    const person = offered[index];
    if (!mention || !person) return;
    const name = nameOf(person);
    const next = insertMention(value, mention.start, caret, name);
    pendingCaret.current = next.caret;
    onMention({ id: person.id, name });
    onValueChange(next.text);
    setActive(0);
    // That `@` is answered: the name after it is not a new question.
    setDismissed({ start: mention.start, query: `${name} ` });
  }

  return (
    <div className="relative">
      <Textarea
        {...props}
        ref={textarea}
        value={value}
        role={open ? 'combobox' : undefined}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={
          open && offered.length > 0 ? `${listId}-${String(active)}` : undefined
        }
        onChange={(event) => {
          onValueChange(event.target.value);
          setCaret(event.target.selectionStart);
          setActive(0);
        }}
        onSelect={(event) => {
          setCaret(event.currentTarget.selectionStart);
        }}
        onKeyDown={(event) => {
          if (open && offered.length > 0) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              const step = event.key === 'ArrowDown' ? 1 : -1;
              setActive((i) => (i + step + offered.length) % offered.length);
              return;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
              event.preventDefault();
              choose(active);
              return;
            }
          }
          if (open && event.key === 'Escape') {
            event.preventDefault();
            setDismissed({ start: mention.start, query: mention.query });
            return;
          }
          onKeyDown?.(event);
        }}
      />
      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-label="People to mention"
          className="bg-popover text-popover-foreground shadow-floating absolute top-full left-0 z-20 mt-1 max-h-64 w-64 overflow-y-auto rounded-md p-1 ring-1 ring-foreground/10"
        >
          {offered.length === 0 ? (
            <li className="text-muted-foreground px-2 py-1.5 text-sm">
              No one matches.
            </li>
          ) : (
            offered.map((person, index) => (
              <li
                key={person.id}
                id={`${listId}-${String(index)}`}
                role="option"
                aria-selected={index === active}
                // Chosen on mouse down, before the textarea loses focus.
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(index);
                }}
                className={cn(
                  'flex cursor-pointer flex-col rounded-sm px-2 py-1.5',
                  index === active ? 'bg-accent' : undefined,
                )}
              >
                <span className="text-foreground text-sm">
                  {nameOf(person)}
                </span>
                <span className="text-muted-foreground text-xs">
                  {person.email}
                </span>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
